/* eslint-disable react/prop-types */
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * DEFAULT_SANDBOX follows the IPM integration spec:
 *  allow-scripts                        — JS must run for the IPM React app to function
 *  allow-same-origin                    — required so the iframe retains its origin for
 *                                         authenticated API calls and cookies
 *  allow-forms                          — required for IPM internal form actions
 *  allow-top-navigation-by-user-activation — allows the iframe to navigate the parent
 *                                         page ONLY on an explicit user gesture (e.g.
 *                                         button click). Safer than allow-top-navigation
 *                                         because it cannot be triggered programmatically.
 *  allow-popups                         — required for window.open() / target="_blank"
 *                                         CTA buttons inside the IPM content to work.
 *  allow-popups-to-escape-sandbox       — ensures new tabs opened by the iframe are NOT
 *                                         themselves sandboxed (required for external CTAs
 *                                         to function correctly in the opened page).
 *  allow-top-navigation / allow-pointer-lock are intentionally OMITTED so a compromised
 *  iframe cannot redirect the parent page arbitrarily or capture mouse input.
 */
export const DEFAULT_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-top-navigation-by-user-activation allow-popups allow-popups-to-escape-sandbox';

/**
 * IPM_LOAD_TIMEOUT_MS is read dynamically from window.env (injected at
 * runtime by env.sh → envconfig.json) so the timeout can be tuned per
 * environment without a code rebuild. Falls back to 60 000 ms (60 s).
 */
const IPM_LOAD_TIMEOUT_MS = Number(
  typeof window !== 'undefined' && window.env && window.env.IPM_LOAD_TIMEOUT_MS
    ? window.env.IPM_LOAD_TIMEOUT_MS
    : 60000,
);

/**
 * Hardcoded URL allowlist — compiled into source, cannot be overridden at
 * runtime via window.env or props. Even if an attacker injects a malicious
 * value into window.env.IPM_FULLLENGTH_AD_URL the POST is blocked before
 * it fires and no user data reaches an unintended server.
 */
export const HARDCODED_ALLOWED_PREFIXES = [
  'https://ngapps-fit.nj.adp.com/apps/ipm/',
  'https://ngapps-iat.adp.com/apps/ipm/app/fulllength-ad',
  'https://ngapps.adp.com/apps/ipm/app/fulllength-ad',
  'http://localhost:3001/apps/ipm/',
];

export function isUrlAllowed(url) {
  const candidate = String(url || '');
  return HARDCODED_ALLOWED_PREFIXES.some((prefix) => candidate.startsWith(prefix));
}

/**
 * IpmAdIframe
 *
 * Loads IPM (In-Product Marketing) advertising content inside a sandboxed
 * iframe by submitting a hidden POST form targeting the iframe by name.
 *
 * POST form fields (per IPM integration spec):
 *  - page    : current page identifier (e.g. "login")
 *  - promoID : optional promo ID passed by the caller (default: "")
 *
 * Error / timeout handling:
 *  - If the iframe does not fire `load` within 3 s, `onSubmitError` is called
 *    and the component hides itself.
 *  - If `onError` fires on the iframe, `onSubmitError` is called immediately.
 */
function IpmAdIframe({
  iframeId,
  iframeName,
  iframeTitle,
  actionUrl,
  page,
  promoID,
  className,
  sandbox,
  onSubmitError,
}) {
  const formRef = useRef(null);
  const timeoutRef = useRef(null);
  const [hidden, setHidden] = React.useState(false);
  const formSubmitted = useRef(false);

  const sandboxAttr = sandbox === false ? DEFAULT_SANDBOX : sandbox;

  useEffect(() => {
    if (!actionUrl || !formRef.current) return undefined;

    formSubmitted.current = false;

    // Security: block POST to any URL not on the hardcoded allowlist
    if (!isUrlAllowed(actionUrl)) {
      const error = new Error(
        `Blocked iframe POST to non-allowlisted actionUrl: ${actionUrl}`,
      );
      // eslint-disable-next-line no-console
      console.error('[IpmAdIframe]', error.message);
      onSubmitError && onSubmitError(error); // eslint-disable-line no-unused-expressions
      return undefined; // form.submit() never called
    }

    let cancelled = false;

    // Preflight HEAD check — verifies the IPM endpoint is reachable and
    // returns a 2xx before we commit to loading it inside the iframe.
    // This prevents 4xx / 5xx error pages from silently rendering in the
    // sandboxed iframe and keeps the 50/50 split layout intact only when
    // IPM is genuinely healthy.
    // 'no-cors' mode is intentionally NOT used: we need to read the status.
    // If the IPM server does not send CORS headers the fetch rejects with a
    // network error, which is also treated as a failure and hides the panel.
    fetch(actionUrl, { method: 'HEAD', credentials: 'include' })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          // 4xx / 5xx — hide the panel gracefully
          const httpError = new Error(
            `IPM preflight failed with status ${res.status}`,
          );
          // eslint-disable-next-line no-console
          console.error('[IpmAdIframe]', httpError.message);
          setHidden(true);
          onSubmitError && onSubmitError(httpError); // eslint-disable-line no-unused-expressions
          return;
        }

        // Submit the hidden form into the iframe (POST)
        try {
          formRef.current.submit();
          formSubmitted.current = true;
        } catch (submitError) {
          onSubmitError && onSubmitError(submitError); // eslint-disable-line no-unused-expressions
          return;
        }

        // Start timeout — hide panel if content doesn't load in time
        timeoutRef.current = setTimeout(() => {
          // eslint-disable-next-line no-console
          console.log('[IpmAdIframe] Timed out – hiding panel');
          setHidden(true);
          onSubmitError && onSubmitError(new Error('IPM iframe load timeout')); // eslint-disable-line no-unused-expressions
        }, IPM_LOAD_TIMEOUT_MS);
      })
      .catch((networkError) => {
        if (cancelled) return;
        // Network failure / CORS rejection — hide the panel gracefully
        // eslint-disable-next-line no-console
        console.error(
          '[IpmAdIframe] Preflight network error:',
          networkError.message,
        );
        setHidden(true);
        onSubmitError && onSubmitError(networkError); // eslint-disable-line no-unused-expressions
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionUrl]);

  const handleLoad = () => {
    // Ignore the initial about:blank load that fires before form submission
    if (!formSubmitted.current) return;
    clearTimeout(timeoutRef.current);
  };

  const handleError = (err) => {
    clearTimeout(timeoutRef.current);
    setHidden(true);
    onSubmitError && onSubmitError(err);
  };

  if (!actionUrl || hidden) return null;

  return (
    <div
      className={ `${className} flex flex-col` }
      data-testid="ipm-ad-iframe-container"
    >
      { /* Hidden form — POSTs to actionUrl per IPM integration spec */ }
      <form
        id={ `${iframeId}-form` }
        ref={ formRef }
        action={ actionUrl }
        method="POST"
        target={ iframeName }
        style={ { display: 'none' } }
        data-testid="ipm-ad-form"
      >
        <input
          type="hidden"
          name="page"
          value={ page }
        />
        <input
          type="hidden"
          name="promoID"
          value={ promoID }
        />
      </form>

      <iframe
        id={ iframeId }
        name={ iframeName }
        title={ iframeTitle }
        className="flex-1 w-full border-0"
        sandbox={ sandboxAttr }
        loading="eager"
        referrerPolicy="no-referrer"
        onLoad={ handleLoad }
        onError={ handleError }
        data-testid="ipm-ad-iframe"
        aria-label={ iframeTitle }
      />
    </div>
  );
}

IpmAdIframe.propTypes = {
  iframeId: PropTypes.string.isRequired,
  iframeName: PropTypes.string.isRequired,
  iframeTitle: PropTypes.string.isRequired,
  actionUrl: PropTypes.string,
  page: PropTypes.string.isRequired,
  /** Optional promo identifier passed to IPM (default empty string per spec) */
  promoID: PropTypes.string,
  className: PropTypes.string,
  /** false = use DEFAULT_SANDBOX; string = custom sandbox attribute value */
  sandbox: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onSubmitError: PropTypes.func,
};

IpmAdIframe.defaultProps = {
  actionUrl: null,
  promoID: '',
  className: '',
  sandbox: false,
  onSubmitError: null,
};

export default IpmAdIframe;
